// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// search.ts — the opponent. A deep abstract with a weak bot is a pointless game, so this is a real
// engine: negamax with alpha-beta, iterative deepening, a transposition table, killer and history
// move ordering, mate-distance scoring and a forward prune that is worth about two plies.
//
// WHY ALPHA-BETA AND NOT MCTS. Brood has no captures and no material, so a random rollout does not
// terminate — two random movers walk Striders round the rim until the ply cap and hand back a coin
// flip. A useful MCTS here would need a trained policy, which this factory cannot ship (no CDN, no
// weights file, nothing to train on). Meanwhile the evaluation is close to a direct reading of the
// win condition — "how many of the six cells around her are filled" — and the tactics are narrow
// forcing nets that alpha-beta solves exactly and prefers to solve fastest. So: depth, not
// sampling.
//
// TWO THINGS THAT LOOK LIKE OVERSIGHTS AND ARE NOT.
//   * There is NO null-move pruning. A pass is a LEGAL MOVE in brood, so the assumption null-move
//     rests on — "doing nothing is never better than doing something" — is simply false here. Do
//     not add it.
//   * The budget is a WALL CLOCK in the shipped game and a NODE COUNT in the sim. A phone has to
//     answer in a fixed time whatever its speed; a test has to produce the same number on every
//     machine. Both budgets exist and neither is allowed to replace the other.
//
// THE FORWARD PRUNE is the reason a phone reaches useful depth. Unpruned branching in the middle
// game is around 100 (two Striders alone see ~40 rim cells), which caps a 900ms search at four
// plies. But nothing outside the two rings around the two Matriarchs can change the score this
// ply: a Strider strolling to the far side of the cluster is a waiting move. So at interior nodes
// two or more plies from the root, a move is kept only if it touches one of those rings, unpins
// something, or is a Matriarch move. Branching falls to roughly 25-35 — worth two plies. The root
// and its children are exempt, so a genuine one-move tactic can never be pruned away.

import { DIR_OFF, distance } from './hex';
import { CLIMBER, MATRIARCH, STRIDER, VAULTER, tileKind } from './kinds';
import type { Mode } from './modes';
import {
  type Move,
  type Position,
  apply,
  computeCuts,
  genMoves,
  liftable,
  matriarchForced,
  outcome,
  plyOf,
  surrounded,
  undo,
} from './rules';

export const MATE = 100000;
const MAX_PLY = 64;
/** How much worse than a draw a repetition is scored, from the point of view of the side that
 *  would be repeating. Roughly one third of a closed side early on. */
const CONTEMPT = 25;

export interface Skill {
  readonly id: string;
  readonly name: string;
  readonly blurb: string;
  /** Nominal search depth. Iterative deepening stops here. */
  readonly depth: number;
  /** Hard node ceiling. The sim uses this alone; the game uses it as a backstop. */
  readonly nodes: number;
  /** Wall-clock ceiling in ms. ZERO in the sim, so its numbers are the same on every machine. */
  readonly timeMs: number;
  /** Root-only score jitter, in centi-units of one filled Matriarch side. NEVER applied to a mate
   *  score, or the bot would decline a win it can see and the audit could not assert it never does. */
  readonly noise: number;
  /**
   * THE NEGATIVE-GAP CONTROL ARM, and nothing else — no shipped tier ever sets it. A bot with this
   * on searches to the same depth with the same evaluation, except that pins and mobility score
   * ZERO: it cannot tell that it has jammed the enemy solid, or that it is jammed itself. brood's
   * signature is that the board is a structure you can seize up, so if this arm does NOT lose
   * badly, the mechanic is decorative and the game is something else.
   *
   * It is a property of the SEARCH, deliberately not of the position, because a flag carried on
   * game state is a flag a state copy silently resets — and a control arm that quietly turns
   * itself off produces numbers identical to the treatment, which reads as a strict result and is
   * actually a broken experiment.
   */
  readonly blindToPins?: boolean;
  /**
   * A PENALTY THE SIM DELETED, kept only so a test can keep it deleted.
   *
   * brood's most brood-specific idea is that a Matriarch is smothered by her own kin exactly as
   * dead as by the enemy's, so the design called for a static penalty on your own tiles beside
   * your own Matriarch — steeper in Cinch, where five sides is enough. It was written, and then
   * measured: a bot with the penalty scored 0.40 / 0.35 / 0.50 against an otherwise identical bot
   * without it, across clutch / retinue / cinch. The penalty made the bot WORSE in two modes out
   * of three and did nothing in the third, because the search already plays through the
   * consequence and the static term only talks it out of useful development near its own
   * Matriarch. So the shipped weight is ZERO, and balance.test.ts asserts that turning it back on
   * measurably hurts — a deleted idea with a test holding the door shut.
   */
  readonly selfBlockWeight?: number;
}

/**
 * The three tiers are a SEPARATE picker from the three modes, deliberately. Weld them together and
 * no measurement can ever say whether a result came from the rule or from the search depth.
 *
 * The noise figures are small on purpose. One closed side of a Matriarch is worth between 8 and 200
 * on this scale depending on how many are already closed, so jitter above about 25 stops being a
 * tie-break and starts randomising the decision that wins the game — which reads as a bot that is
 * not so much weak as broken.
 */
export const SKILLS: Record<string, Skill> = {
  hatchling: {
    id: 'hatchling',
    name: 'Hatchling',
    blurb: 'Fills and blocks a move at a time. Will not see a net closing.',
    depth: 2,
    nodes: 40_000,
    timeMs: 400,
    noise: 22,
  },
  forager: {
    id: 'forager',
    name: 'Forager',
    blurb: 'Reads the ring properly and punishes a loose Matriarch.',
    depth: 5,
    nodes: 400_000,
    timeMs: 700,
    noise: 4,
  },
  broodmother: {
    id: 'broodmother',
    name: 'Broodmother',
    blurb: 'Searches deep, and closes nets three moves before you see them.',
    depth: 8,
    nodes: 2_500_000,
    timeMs: 950,
    noise: 0,
  },
};

export const SKILL_IDS: readonly string[] = ['hatchling', 'forager', 'broodmother'];

export function skillOf(id: unknown): Skill {
  if (typeof id === 'string' && Object.hasOwn(SKILLS, id)) return SKILLS[id];
  return SKILLS.forager;
}

// ---------------------------------------------------------------- evaluation

/**
 * What each additional closed side of a Matriarch is worth. Convex, so the last one before the
 * kill is worth far more than the first — which is what makes the bot press an attack home instead
 * of spreading pressure evenly. Built per mode, because Cinch ends at five and its ramp has to
 * arrive a side earlier.
 */
function ramp(surroundAt: number): number[] {
  const out: number[] = [];
  for (let n = 0; n <= 6; n++) out.push(n >= surroundAt ? 900 : Math.round(160 * Math.pow(n / surroundAt, 2.4)));
  return out;
}

const RAMPS = new Map<number, number[]>();
const rampFor = (m: Mode): number[] => {
  let r = RAMPS.get(m.surroundAt);
  if (!r) {
    r = ramp(m.surroundAt);
    RAMPS.set(m.surroundAt, r);
  }
  return r;
};

const W_PINNED = 9; // a piece that cannot be lifted is a piece that is not playing
const W_MOBILE = 2; // raw mobility, small — depth reads this better than the eval does
const W_COVER = 70; // a Climber sitting on a Matriarch pins her where she stands
const W_RESERVE = 4; // tiles still in hand are options
const W_STRIDER = 7; // ...and a Strider in hand is the best of them

/** Set only by chooseMove, for the duration of one search. See Skill.blindToPins. */
let blindToPins = false;
/** Zero in every shipped tier. See Skill.selfBlockWeight for the measurement that put it there. */
let selfBlockWeight = 0;

/** Static evaluation, from the point of view of the side to move. */
export function evaluate(p: Position): number {
  const me = p.turn;
  const you = me ^ 1;
  const r = rampFor(p.mode);

  let s = 0;

  // The ring. Note both sides' rings count tiles of EITHER colour — a Matriarch is smothered by
  // her own kin just as dead as by the enemy's, which is exactly what makes Cinch a different game.
  const mineAt = p.matriarch[me];
  const yoursAt = p.matriarch[you];
  if (yoursAt >= 0) {
    let closed = 0;
    for (let d = 0; d < 6; d++) if (p.occ[yoursAt + DIR_OFF[d]] !== 0) closed++;
    s += r[closed];
    if (p.occ[yoursAt] > 1) s += W_COVER;
  }
  if (mineAt >= 0) {
    let closed = 0;
    let mineOwn = 0;
    for (let d = 0; d < 6; d++) {
      const nb = mineAt + DIR_OFF[d];
      if (p.occ[nb] === 0) continue;
      closed++;
      if ((p.topT[nb] & 1) === me) mineOwn++;
    }
    s -= r[closed];
    if (selfBlockWeight !== 0) s -= selfBlockWeight * mineOwn;
    if (p.occ[mineAt] > 1) s -= W_COVER;
  }

  // Mobility and pins. A tile whose lifting would split the cluster is frozen where it stands, and
  // a frozen attacker is not an attacker.
  if (!blindToPins) {
    computeCuts(p);
    let myMobile = 0;
    let yourMobile = 0;
    let myPinned = 0;
    let yourPinned = 0;
    for (let i = 0; i < p.n; i++) {
      const k = p.list[i];
      const seat = p.topT[k] & 1;
      const free = liftable(p, k) && !p.stunned(k);
      if (seat === me) {
        if (free) myMobile++;
        else myPinned++;
      } else {
        if (free) yourMobile++;
        else yourPinned++;
      }
    }
    s += W_MOBILE * (myMobile - yourMobile) + W_PINNED * (yourPinned - myPinned);
  }

  // Reserves. Tiles in hand are choices, and the Strider is the piece that actually closes rings.
  let myHand = 0;
  let yourHand = 0;
  for (let k = 0; k < p.hands[me].length; k++) {
    myHand += p.hands[me][k];
    yourHand += p.hands[you][k];
  }
  s += W_RESERVE * (myHand - yourHand);
  s += W_STRIDER * (p.hands[me][STRIDER] - p.hands[you][STRIDER]);

  return s;
}

// ---------------------------------------------------------------- transposition table

const TT_BITS = 18;
const TT_SIZE = 1 << TT_BITS;
const ttKey = new Int32Array(TT_SIZE);
const ttScore = new Int32Array(TT_SIZE);
const ttDepth = new Int8Array(TT_SIZE);
const ttFlag = new Int8Array(TT_SIZE); // 0 empty, 1 exact, 2 lower, 3 upper
const ttMove = new Int32Array(TT_SIZE);
const ttGen = new Int8Array(TT_SIZE);
let generation = 0;

// Zobrist. One 32-bit word per (cell, tile) and a second layer for a covered tile — stacks are
// rare, so a single extra layer is enough to keep two different stacks apart.
const ZOB = new Int32Array(16384 * 12);
const ZOB_UNDER = new Int32Array(16384 * 12);
let ZTURN = 0;
{
  // A fixed, cheap, well-mixing generator. Deterministic across machines and runs, which the
  // lockstep replay and the sim's byte-identical numbers both need.
  let x = 0x9e3779b9;
  const next = (): number => {
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    return x | 0;
  };
  for (let i = 0; i < ZOB.length; i++) ZOB[i] = next();
  for (let i = 0; i < ZOB_UNDER.length; i++) ZOB_UNDER[i] = next();
  ZTURN = next();
}

/** A 32-bit hash of the position. Order-independent (it is an xor over cells), so no sort. */
export function hashOf(p: Position): number {
  let h = 0;
  for (let i = 0; i < p.n; i++) {
    const k = p.list[i];
    h ^= ZOB[k * 12 + p.topT[k]];
    if (p.occ[k] > 1) {
      const st = p.deep.get(k)!;
      for (let j = 0; j < st.length - 1; j++) h ^= ZOB_UNDER[k * 12 + st[j]];
    }
  }
  if (p.turn === 1) h ^= ZTURN;
  if (p.ply < p.stunUntil) h ^= (p.stunA * 2654435761) ^ (p.stunB * 40503);
  return h | 0;
}

// ---------------------------------------------------------------- move ordering

const killers = new Int32Array(MAX_PLY * 2);
const history = new Map<number, number>();
const pathHash = new Int32Array(MAX_PLY + 8);

/** A cheap static ordering score. The whole point is to try ring-filling moves first. */
function orderScore(p: Position, m: Move, ttBest: number, depthPly: number): number {
  const ply = plyOf(m);
  if (ply === ttBest) return 1_000_000;
  if (ply === killers[depthPly * 2] || ply === killers[depthPly * 2 + 1]) return 500_000;

  const you = p.turn ^ 1;
  const theirs = p.matriarch[you];
  const mine = p.matriarch[p.turn];
  let s = history.get(ply) ?? 0;
  const to = m.t === 'pass' ? -1 : m.to;
  if (to >= 0) {
    if (theirs >= 0) {
      const d = distance(to, theirs);
      if (d === 1) s += 9000;
      else if (d === 2) s += 1200;
    }
    if (mine >= 0 && distance(to, mine) === 1) s += 300;
  }
  if (m.t === 'move' && mine >= 0 && distance(m.from, mine) === 1) s += 200;
  return s;
}

/**
 * The forward prune. Only applied at interior nodes two or more plies below the root, where a move
 * that touches neither ring provably cannot change this ply's score.
 */
function relevant(p: Position, m: Move): boolean {
  if (m.t === 'pass') return true;
  const mine = p.matriarch[p.turn];
  const theirs = p.matriarch[p.turn ^ 1];
  if (m.t === 'place') {
    return (
      (theirs >= 0 && distance(m.to, theirs) <= 2) ||
      (mine >= 0 && distance(m.to, mine) <= 2)
    );
  }
  if (m.t === 'shunt') return true; // the Tender's whole purpose is to undo a ring
  const kind = tileKind(p.topT[m.from]);
  if (kind === MATRIARCH) return true; // her escape square is never a waiting move
  if (theirs >= 0 && distance(m.to, theirs) <= 1) return true;
  if (mine >= 0 && distance(m.to, mine) <= 1) return true;
  if (mine >= 0 && distance(m.from, mine) <= 1) return true; // vacating my own ring is a decision
  if (theirs >= 0 && distance(m.from, theirs) <= 1) return true;
  // The two pieces that ignore the slide gate are the sharpest tactical tools in the game, so they
  // are kept a ring wider than everything else.
  if ((kind === VAULTER || kind === CLIMBER) && theirs >= 0 && distance(m.to, theirs) <= 2) return true;
  return false;
}

// ---------------------------------------------------------------- the search

interface Ctx {
  nodes: number;
  cap: number;
  deadline: number;
  aborted: boolean;
}

let ctx: Ctx = { nodes: 0, cap: 0, deadline: 0, aborted: false };

function terminal(p: Position, ply: number): number | null {
  if (surrounded(p, p.turn) && surrounded(p, p.turn ^ 1)) return 0;
  if (surrounded(p, p.turn)) return -(MATE - ply);
  if (surrounded(p, p.turn ^ 1)) return MATE - ply;
  if (p.passes >= 2) return 0;
  if (p.ply >= p.mode.plyCap) return 0;
  return null;
}

function negamax(p: Position, depth: number, alpha: number, beta: number, ply: number, ext: number): number {
  const t = terminal(p, ply);
  if (t !== null) return t;

  if ((ctx.nodes & 1023) === 0 && ctx.deadline > 0 && Date.now() > ctx.deadline) ctx.aborted = true;
  if (ctx.aborted || ctx.nodes >= ctx.cap) return evaluate(p);
  ctx.nodes++;

  const h = hashOf(p);
  // A position seen earlier on this line is a repetition, and a repetition is a draw — which is
  // what stops the search from "winning" by shuffling. It is scored slightly WORSE than a draw
  // (contempt), so a bot that is behind plays on and makes the human finish the job instead of
  // shuffling them into a draw sixty plies deep. It also converts a chunk of the drawn games the
  // baseline sweep measured, without touching a single rule.
  for (let i = ply - 2; i >= 0; i -= 2) if (pathHash[i] === h) return -CONTEMPT;
  pathHash[ply] = h;

  // The control arm evaluates differently, so its scores must never be read back by the sighted
  // arm. Folding the flag into the table key partitions the two rather than trusting a generation
  // counter that only governs writes.
  const th = (blindToPins ? (h ^ 0x5bd1e995) | 0 : h) ^ (selfBlockWeight !== 0 ? 0x27d4eb2d : 0);
  const idx = (th >>> (32 - TT_BITS)) & (TT_SIZE - 1);
  let ttBest = 0;
  if (ttFlag[idx] !== 0 && ttKey[idx] === th) {
    ttBest = ttMove[idx];
    if (ttDepth[idx] >= depth) {
      const s = ttScore[idx];
      const f = ttFlag[idx];
      if (f === 1) return s;
      if (f === 2 && s >= beta) return s;
      if (f === 3 && s <= alpha) return s;
    }
  }

  // A one-ply extension when a Matriarch is one side from being closed. That is where every
  // horizon error in this game lives: a position that reads as won at five of six is refuted by a
  // single step, and in Retinue by a single lift over the Tender.
  //
  // THE BUDGET IS SPENT, NOT RENEWED. An earlier cut of this decremented `ext` only on the branch
  // that did NOT extend, so an extended node handed its children the full budget again and a line
  // sitting at five-of-six extended forever — a search that never returned rather than one that
  // returned a wrong answer, which is why it showed up as "the sim is slow" instead of as a bug.
  let d = depth;
  let nextExt = ext;
  if (ext > 0 && d <= 0) {
    const at = p.mode.surroundAt - 1;
    let closedMine = 0;
    let closedYours = 0;
    const mi = p.matriarch[p.turn];
    const yi = p.matriarch[p.turn ^ 1];
    for (let k = 0; k < 6; k++) {
      if (mi >= 0 && p.occ[mi + DIR_OFF[k]] !== 0) closedMine++;
      if (yi >= 0 && p.occ[yi + DIR_OFF[k]] !== 0) closedYours++;
    }
    if (closedMine >= at || closedYours >= at) {
      d = 1;
      nextExt = ext - 1;
    }
  }
  if (d <= 0) return evaluate(p);

  const moves = genMoves(p, []);
  const prune = ply >= 2 && moves.length > 24;
  const scored: Array<{ m: Move; s: number }> = [];
  for (let i = 0; i < moves.length; i++) {
    const m = moves[i];
    if (prune && !relevant(p, m)) continue;
    scored.push({ m, s: orderScore(p, m, ttBest, ply) });
  }
  if (scored.length === 0) for (let i = 0; i < moves.length; i++) scored.push({ m: moves[i], s: 0 });
  scored.sort((a, b) => b.s - a.s || plyOf(a.m) - plyOf(b.m));

  let best = -MATE * 2;
  let bestPly = 0;
  const a0 = alpha;
  for (let i = 0; i < scored.length; i++) {
    const m = scored[i].m;
    const rec = apply(p, m);
    const s = -negamax(p, d - 1, -beta, -alpha, ply + 1, nextExt);
    undo(p, rec);
    if (ctx.aborted) return best > -MATE * 2 ? best : evaluate(p);
    if (s > best) {
      best = s;
      bestPly = plyOf(m);
    }
    if (s > alpha) alpha = s;
    if (alpha >= beta) {
      if (m.t !== 'place') {
        killers[ply * 2 + 1] = killers[ply * 2];
        killers[ply * 2] = bestPly;
      }
      history.set(bestPly, (history.get(bestPly) ?? 0) + d * d);
      break;
    }
  }

  if (ttDepth[idx] <= d || ttGen[idx] !== generation) {
    ttKey[idx] = th;
    ttScore[idx] = best;
    ttDepth[idx] = d;
    ttFlag[idx] = best <= a0 ? 3 : best >= beta ? 2 : 1;
    ttMove[idx] = bestPly;
    ttGen[idx] = generation;
  }
  return best;
}

export interface Choice {
  readonly move: Move;
  readonly score: number;
  readonly depth: number;
  readonly nodes: number;
  readonly ms: number;
}

/**
 * Pick a move. `rand` is only ever consulted for the root jitter that gives the weaker tiers their
 * character; with `noise: 0` the whole function is deterministic, which is what lets the sim assert
 * byte-identical replays.
 */
export function chooseMove(p: Position, skill: Skill, rand: () => number = Math.random): Choice | null {
  if (outcome(p).over) return null;
  const started = Date.now();
  generation = (generation + 1) & 127;
  history.clear();
  killers.fill(0);
  // Both control-arm settings are folded into the transposition key rather than relying on the
  // generation counter, which only governs REPLACEMENT on writes and never validity on reads.
  blindToPins = skill.blindToPins === true;
  selfBlockWeight = skill.selfBlockWeight ?? 0;

  const root = genMoves(p, []);
  root.sort((a, b) => plyOf(a) - plyOf(b));
  if (root.length === 0) return null;
  if (root.length === 1) return { move: root[0], score: 0, depth: 0, nodes: 0, ms: 0 };

  ctx = { nodes: 0, cap: skill.nodes, deadline: skill.timeMs > 0 ? started + skill.timeMs : 0, aborted: false };

  let best = root[0];
  let bestScore = -MATE * 2;
  let reached = 0;

  for (let depth = 1; depth <= skill.depth; depth++) {
    let localBest = root[0];
    let localScore = -MATE * 2;
    let alpha = -MATE * 2;
    const ordered = depth === 1 ? root : orderRoot(p, root, best);
    for (const m of ordered) {
      const rec = apply(p, m);
      const s = -negamax(p, depth - 1, -MATE * 2, -alpha, 1, 2);
      undo(p, rec);
      if (ctx.aborted) break;
      // Jitter is a TIE-BREAK, and it is never allowed near a decided score: a bot that declines a
      // win it can see reads as broken, and the audit asserts it never does.
      const jitter = skill.noise > 0 && Math.abs(s) < MATE / 2 ? (rand() - 0.5) * 2 * skill.noise : 0;
      const adj = s + jitter;
      if (adj > localScore) {
        localScore = adj;
        localBest = m;
      }
      if (s > alpha) alpha = s;
    }
    if (ctx.aborted && reached > 0) break;
    best = localBest;
    bestScore = localScore;
    reached = depth;
    if (ctx.aborted) break;
    if (bestScore >= MATE / 2) break; // a forced win is found; deeper cannot improve on it
    if (ctx.nodes >= ctx.cap) break;
  }

  return { move: best, score: bestScore, depth: reached, nodes: ctx.nodes, ms: Date.now() - started };
}

function orderRoot(p: Position, root: readonly Move[], first: Move): Move[] {
  const fp = plyOf(first);
  return root.slice().sort((a, b) => {
    const pa = plyOf(a);
    const pb = plyOf(b);
    if (pa === fp) return -1;
    if (pb === fp) return 1;
    return orderScore(p, b, 0, 0) - orderScore(p, a, 0, 0) || pa - pb;
  });
}

/**
 * A one-ply greedy control, used by the balance sim as the floor a real engine has to beat
 * decisively. It is also what the "is the search load-bearing" gap is measured against.
 */
export function greedyMove(p: Position, rand: () => number = Math.random): Move | null {
  const moves = genMoves(p, []);
  if (moves.length === 0) return null;
  moves.sort((a, b) => plyOf(a) - plyOf(b));
  let best = moves[0];
  let bestScore = -Infinity;
  for (const m of moves) {
    const rec = apply(p, m);
    const t = terminal(p, 1);
    const s = t !== null ? -t : -evaluate(p);
    undo(p, rec);
    const adj = s + (rand() - 0.5) * 0.001;
    if (adj > bestScore) {
      bestScore = adj;
      best = m;
    }
  }
  return best;
}

/** A legal-random control — the absolute floor. */
export function randomMove(p: Position, rand: () => number = Math.random): Move | null {
  const moves = genMoves(p, []);
  if (moves.length === 0) return null;
  moves.sort((a, b) => plyOf(a) - plyOf(b));
  return moves[Math.floor(rand() * moves.length) % moves.length];
}

/** Exposed so the HUD can explain why a placement is forced. */
export const matriarchIsForced = matriarchForced;
